// @flow
/* eslint-disable react/no-unused-prop-types */
/** @jsx jsx */

import { jsx, css, ClassNames } from '@emotion/core';
import * as React from 'react';
import Calendar from 'rc-calendar';
import DatePicker from 'rc-calendar/lib/Picker';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import HelpBlock from 'react-bootstrap/lib/HelpBlock';
import moment from 'moment';
import * as input from 'wijmo/wijmo.input';
import { InputDate } from 'wijmo/wijmo.react.input';
import 'rc-calendar/assets/index.css';
import LabelCell from './LabelCell';
import FormRow from './FormRow';
import { getDateFormat } from '../utils';
import { MobileView, DesktopView } from '../config/mediaQuery';

type Props = {
  label: string,
  onChange: (d: moment) => mixed,
  disabled?: boolean,
  value: moment | null,
  placeholderField?: string,
  placeholderCalendar?: string,
  disabledDate?: any => any => boolean,
  name: string,
  holidays: Array<string>,
  disabledDays: Array<number> | [],
  minSelectableDay: string,
  maxSelectableDay: string,
  setFieldValue?: (field: string, val: any) => mixed,
  setFieldTouched?: (field: string, val: any) => mixed,
  errors?: {
    [name: string]: string,
  },
  touched?: {
    [name: string]: string,
  },
  className?: any,
  inputClassName?: any,
  labelClassName?: any,
  inputContainerClassName?: any,
  showErrorMsg?: boolean,
  visible: boolean,
  mobileModal: Boolean,
  isDateValid: Boolean,
};

const defaultDisabledDate = (props: Props) => (current: moment) => {
  if (!current) {
    // allow empty select
    return false;
  }

  const a = current.day();
  const typeFormat = getDateFormat('server');
  // 0 = Sunday
  // 6 = Saturday
  const isWeekend = a === 0 || a === 6;
  const isHoliday = props.holidays.includes(current.format(typeFormat));
  const isDisabledDay = props.disabledDays.includes(a);
  const isBeforeMinDate = current.isBefore(
    moment(props.minSelectableDay, typeFormat),
    'day',
  );

  const isAfterMaxDate = current.isAfter(
    moment(props.maxSelectableDay, typeFormat),
    'day',
  );

  if (
    isWeekend ||
    isHoliday ||
    isDisabledDay ||
    isBeforeMinDate ||
    isAfterMaxDate
  ) {
    return true;
  }

  const date = moment();
  date.hour(0);
  date.minute(0);
  date.second(0);
  return current.valueOf() < date.valueOf(); // can not select days before today
};

const forceEnabledField = css`
  background-color: white !important;
  min-width: 86px;
`;

const defaultStyle = css`
  margin-bottom: 15px;
`;

const hiddenStyle = css`
  visibility: hidden;
  opacity: 0;
`;

export default class DatePickerField extends React.Component<Props> {
  constructor(props) {
    super(props)
    this.calendarRef = React.createRef()
  }

  static defaultProps = {
    value: undefined,
    placeholderField: '',
    placeholderCalendar: '',
    disabledDate: defaultDisabledDate,
    showErrorMsg: true,
    visible: true,
    mobileModal: false,
    isDateValid: true,
  };

  onChange = (value: moment) => {
    const typeFormat = getDateFormat('server');

    if (typeof this.props.setFieldValue === 'function') {
      this.props.setFieldValue(
        this.props.name,
        value ? value.format(typeFormat) : '',
      );
    }

    if (value && value.isValid()) {
      this.props.onChange(value);
    }
  };

  onOpenChange = (opened: boolean) => {
    this.calendarRef.current.click()
    if (typeof this.props.setFieldTouched === 'function' && opened === false) {
      this.props.setFieldTouched(this.props.name, true);
    }
  };

  onChangeMobile = (value) => {
    this.props.setFieldTouched(this.props.name, true);
    const newValue = moment(value._cal._value);
    const typeFormat = getDateFormat('server');
    if (typeof this.props.setFieldValue === 'function') {
      this.props.setFieldValue(
        this.props.name,
        value ? newValue.format(typeFormat) : '',
      );
    }
    if (newValue && newValue.isValid()) {
      this.setState({ open: false })
      this.props.onChange(newValue);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props !== prevProps) {
      const wijmoDatePicker = document.getElementById('orderDate');
      if (wijmoDatePicker && !this.props.isDateValid) {
        wijmoDatePicker.classList.add('date-invalid');
      } else if (wijmoDatePicker && this.props.isDateValid) {
        wijmoDatePicker.classList.remove('date-invalid');
      }
    }
  }

  render() {
    const { mobileModal } = this.props;
    const calendar = (
      <Calendar
        dateInputPlaceholder={this.props.placeholderCalendar}
        formatter={getDateFormat('client')}
        // $FlowFixMe
        disabledDate={this.props.disabledDate(this.props)}
        showToday={false}
      />
    );

    return (
      <FormRow
        css={[
          defaultStyle,
          this.props.className,
          !this.props.visible && hiddenStyle,
          { ...(mobileModal && { alignItems: 'center' }) }
        ]}
      >
        {!mobileModal ?
          <LabelCell css={[this.props.labelClassName]}>
            <ControlLabel>{this.props.label}</ControlLabel>
          </LabelCell>
          :
          <div className='input-label'>
            {this.props.label}
          </div>
        }
        <DesktopView>
          <DatePicker
            style={{
              zIndex: 10000,
            }}
            animation="slide-up"
            disabled={this.props.disabled}
            calendar={calendar}
            value={this.props.value}
            onChange={this.onChange}
            onOpenChange={this.onOpenChange}
          >
            {({ value }) => (
              <div
                css={[
                  {
                    position: 'relative',
                    ...(mobileModal && { flex: 1 })
                  },
                  this.props.inputContainerClassName,
                ]}
              >
                <ClassNames>
                  {({ css, cx }) => {
                    return (
                      <input
                        ref={this.calendarRef}
                        placeholder={this.props.placeholderField}
                        disabled={this.props.disabled}
                        readOnly
                        tabIndex="-1"
                        className={'form-control'}
                        css={[forceEnabledField, this.props.inputClassName]}
                        value={
                          (value && value.format(getDateFormat('clientShort'))) ||
                          ''
                        }
                      />
                    );
                  }}
                </ClassNames>
                {this.props.name &&
                  // $FlowFixMe
                  this.props.errors[this.props.name] &&
                  // $FlowFixMe
                  this.props.touched[this.props.name] &&
                  this.props.showErrorMsg && (
                    <HelpBlock
                      css={{
                        position: 'absolute',
                        bottom: '-30px',
                      }}
                    >
                      {this.props.errors[this.props.name]}
                    </HelpBlock>
                  )}
              </div>
            )}
          </DatePicker>
        </DesktopView>
        <MobileView>
          <InputDate
            id='orderDate'
            value={this.props.value && this.props.value.toDate()}
            itemValidator={(date) => !defaultDisabledDate(this.props)(moment(date))}
            valueChanged={this.onChangeMobile}
            isDisabled={this.props.disabled}
          />
        </MobileView>
      </FormRow>
    );
  }
}
